import { useCallback } from 'react'
import {
	useCompleteDemandRequestLineItemMutation,
	useReopenDemandRequestLineItemMutation,
	useCancelDemandRequestLineItemMutation,
} from '../../../graphql'

type DemandRequest = {
	id: string
}

type UseLineItemActionsParams = {
	request: DemandRequest | undefined | null
	addToast: ( message: string ) => void
}

/**
 * Hook for managing single line item actions (complete, reopen, cancel)
 */
export function useLineItemActions( { request, addToast }: UseLineItemActionsParams ) {
	const [ completeLineItem ] = useCompleteDemandRequestLineItemMutation()
	const [ reopenLineItem ] = useReopenDemandRequestLineItemMutation()
	const [ cancelLineItem ] = useCancelDemandRequestLineItemMutation()

	const handleCompleteLineItem = useCallback( async( lineItemId: string ) => {
		if ( !request ) {
			return
		}

		try {
			const result = await completeLineItem( {
				variables: {
					input: {
						demandRequestId: request.id,
						lineItemId,
					},
				},
			} )

			if ( result.data?.completeDemandRequestLineItem?.success ) {
				addToast( 'Line item marked as completed' )
			} else {
				const errorMessage = result.data?.completeDemandRequestLineItem?.message ?? 'Unknown error occurred'
				addToast( `Failed to complete line item: ${errorMessage}` )
			}
		} catch ( err ) {
			addToast( `Failed to complete line item: ${err instanceof Error ? err.message : 'Unknown error'}` )
		}
	}, [ request, completeLineItem, addToast ] )

	const handleReopenLineItem = useCallback( async( lineItemId: string ) => {
		if ( !request ) {
			return
		}

		try {
			const result = await reopenLineItem( {
				variables: {
					input: {
						demandRequestId: request.id,
						lineItemId,
					},
				},
			} )

			if ( result.data?.reopenDemandRequestLineItem?.success ) {
				addToast( 'Line item reopened' )
			} else {
				const errorMessage = result.data?.reopenDemandRequestLineItem?.message ?? 'Unknown error occurred'
				addToast( `Failed to reopen line item: ${errorMessage}` )
			}
		} catch ( err ) {
			addToast( `Failed to reopen line item: ${err instanceof Error ? err.message : 'Unknown error'}` )
		}
	}, [ request, reopenLineItem, addToast ] )

	const handleCancelLineItem = useCallback( async( lineItemId: string ) => {
		if ( !request ) {
			return
		}

		try {
			const result = await cancelLineItem( {
				variables: {
					input: {
						demandRequestId: request.id,
						lineItemId,
					},
				},
			} )

			if ( result.data?.cancelDemandRequestLineItem?.success ) {
				addToast( 'Line item cancelled' )
			} else {
				const errorMessage = result.data?.cancelDemandRequestLineItem?.message ?? 'Unknown error occurred'
				addToast( `Failed to cancel line item: ${errorMessage}` )
			}
		} catch ( err ) {
			addToast( `Failed to cancel line item: ${err instanceof Error ? err.message : 'Unknown error'}` )
		}
	}, [ request, cancelLineItem, addToast ] )

	return {
		handleCompleteLineItem,
		handleReopenLineItem,
		handleCancelLineItem,
	}
}
